import { FC, useCallback, useState } from 'react';
import {
  fetchQuery,
  useFragment,
  useQueryLoader,
  useRelayEnvironment,
} from 'react-relay';
import graphql from 'babel-plugin-relay/macro';

import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import FlightSearchForm, { FormFields } from 'components/FlightSearchForm';
import PackageDateAddFlightDialogSearchResults from 'components/PackageDateAddFlightDialogSearchResults';

import { useDialog } from 'components/Dialog';
import { dateString } from 'lib';

import Query, {
  PackageDateAddFlightDialogQuery,
} from '__generated__/PackageDateAddFlightDialogQuery.graphql';
import { PackageDateAddFlightDialog_packageDate$key } from '__generated__/PackageDateAddFlightDialog_packageDate.graphql';
import { PackageDateAddFlightDialog_data$key } from '__generated__/PackageDateAddFlightDialog_data.graphql';
import { PackageDateAddFlightDialog_package$key } from '__generated__/PackageDateAddFlightDialog_package.graphql';

type Props = {
  packageDate: PackageDateAddFlightDialog_packageDate$key;
  data: PackageDateAddFlightDialog_data$key;
  package: PackageDateAddFlightDialog_package$key;
  autoStart?: boolean;
};

const PackageDateAddFlightDialog: FC<Props> = props => {
  const { autoStart = false, ...rest } = props;
  const { close } = useDialog();
  const environment = useRelayEnvironment();
  const [searching, setSearching] = useState(false);

  const [queryRef, search] =
    useQueryLoader<PackageDateAddFlightDialogQuery>(graphql`
      query PackageDateAddFlightDialogQuery($params: FlightSearchParams) {
        flightsSearch(params: $params) {
          ...FlightSearchResults_results
        }
      }
    `);

  const handleSearch = useCallback(
    (params: FormFields) => {
      if (searching) {
        return;
      }

      setSearching(true);

      const variables = {
        params: {
          directOnly: params.directOnly,
          departureAirport: params.departureAirport!.code,
          arrivalAirport: params.arrivalAirport!.code,
          departureDate: dateString(params.departureDate)!,
          returnDate: dateString(params.returnDate)!,
          adults: Number(params.adults),
          children: Number(params.children),
          infant: Number(params.infant),
        },
      };

      fetchQuery(environment, Query, variables).subscribe({
        complete: () => {
          setSearching(false);

          search(variables, { fetchPolicy: 'store-or-network' });
        },
        error: () => {
          setSearching(false);
        },
      });
    },
    [environment, searching, search]
  );

  const data = useFragment(
    graphql`
      fragment PackageDateAddFlightDialog_data on Query {
        ...FlightSearchForm_data
      }
    `,
    rest.data
  );

  const { id, start, end } = useFragment(
    graphql`
      fragment PackageDateAddFlightDialog_packageDate on PackageDate {
        id
        start
        end
      }
    `,
    rest.packageDate
  );

  const { defaultDepartureAirport, defaultArrivalAirport } = useFragment(
    graphql`
      fragment PackageDateAddFlightDialog_package on Package {
        defaultDepartureAirport {
          ...FlightSearchForm_defaultDepartureAirport
        }

        defaultArrivalAirport {
          ...FlightSearchForm_defaultArrivalAirport
        }
      }
    `,
    rest.package
  );

  return (
    <>
      <DialogTitle>Skrydžių paieška</DialogTitle>

      <DialogContent dividers>
        <FlightSearchForm
          data={data}
          departureDate={start}
          returnDate={end}
          onSubmit={handleSearch}
          loading={searching}
          defaultDepartureAirport={defaultDepartureAirport}
          defaultArrivalAirport={defaultArrivalAirport}
          autoSubmit={autoStart}
        />

        {queryRef && (
          <PackageDateAddFlightDialogSearchResults
            packageDateID={id}
            queryRef={queryRef}
          />
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={close} variant="text">
          Atšaukti
        </Button>
      </DialogActions>
    </>
  );
};

export default PackageDateAddFlightDialog;
